import { useState, useRef, useEffect } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import { SxProps } from '@mui/material';
import DotCircleLoader from '../../Loader/DotCircleLoader';
import AbsoluteCenter from '../../AbsoluteCenter';

interface ITabPanelProps {
  children?: any;
  index: number;
  value: number;
}

function TabPanelComponent(props: ITabPanelProps) {
  const { children, value, index, ...other } = props;

  return value === index ? children : <></>
}

interface ITabsPanelProps {
  tabs: { 
    label: string, 
    icon?: React.ReactElement,
    badge?: number | string,
    badgeColor?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    disabled?: boolean, 
    component: React.ReactElement | React.ReactNode, 
    shouldRenderComponent?: (...arg: any) => boolean 
  }[],
  onChange?: (newValue: number) => boolean
  sx?: SxProps,
  loading?: boolean,
  delay?: number,
  delayBy?:string,
  currentTab?:number,
}

export default function TabsPanels({ currentTab = 0, tabs, sx, delayBy, delay, onChange, loading, ...other }: ITabsPanelProps) {
  const [value, setValue] = useState(0);

  const timeoutRef: { current: NodeJS.Timeout | null } = useRef(null)
  const [isDelay, setIsDelay] = useState(false)

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (onChange) {
      if (!onChange(newValue)) {
        return 'ok'
      }
    }
    if (tabs[newValue]?.shouldRenderComponent) {
      if (tabs[newValue]?.shouldRenderComponent?.(newValue)) {
        setValue(newValue);
      }
    } else {
      setValue(newValue);
    }
  };

  useEffect(() => {
    if (delay) {
      setIsDelay(true)
      timeoutRef.current = setTimeout(() => {
        setIsDelay(false)
      }, delay)
    }
  }, [delayBy, value, delay])

  useEffect(()=>{
    setValue(currentTab)
  },[currentTab])

  // Helper function to render tab label with icon and badge
  const renderTabLabel = (tab: typeof tabs[0]) => {
    const labelContent = (
      <Box display="flex" alignItems="center" gap={0.75}>
        {tab.icon && (
          <Box sx={{ display: 'flex', alignItems: 'center', '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}>
            {tab.icon}
          </Box>
        )}
        <span>{tab.label}</span>
      </Box>
    )

    if (tab.badge !== undefined && tab.badge !== null && tab.badge !== '') {
      return (
        <Badge 
          badgeContent={tab.badge} 
          color={tab.badgeColor || 'primary'}
          sx={{ 
            '& .MuiBadge-badge': { 
              fontSize: '0.65rem',
              minWidth: 18,
              height: 18,
              padding: '0 4px',
            } 
          }}
        >
          {labelContent}
        </Badge>
      )
    }

    return labelContent
  }

  return (
    <Stack sx={{ height: '100%', width: '100%', ...sx }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
          {tabs.map((tab, index) => (
            <Tab 
              label={renderTabLabel(tab)} 
              disabled={tab.disabled} 
              key={index}
              sx={{
                minHeight: 48,
                textTransform: 'none',
                fontWeight: value === index ? 600 : 400,
              }}
            />
          ))}
        </Tabs>
      </Box>
      <Box flex={1} overflow={'auto'} position={'relative'}>
        {
          loading || isDelay ? (<AbsoluteCenter><DotCircleLoader/></AbsoluteCenter>) :
            tabs?.filter(i => i.component)?.map((tab, index) => (
              <TabPanelComponent value={value} index={index} key={index}>
                {tab.component}
              </TabPanelComponent>
            ))
        }
      </Box>
    </Stack>
  );
}